import React, { useState, useMemo } from 'react';
import { Table, Button, Popconfirm, Input, Space } from 'antd';
import { useCustomer } from '../../store/customerContext';
import { Customer } from '../../types/customer';
import { useTranslation } from "react-i18next";
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../../store/AuthContext';

interface CustomerListProps {
  onEdit: (customer: Customer) => void;
}

const CustomerList: React.FC<CustomerListProps> = ({ onEdit }) => {
  const { customers, deleteCustomer } = useCustomer();
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState<string>('');
  const { hasPermission } = useAuth();

  const getLocalizedCityName = (customer: Customer): string => {
    if (typeof customer.city === 'object') {
      return i18n.language === 'ar'
        ? customer.city.name_ar
        : i18n.language === 'ckb'
          ? customer.city.name_ku
          : customer.city.name_eng;
    }
    return customer.city as unknown as string;
  };

  const filteredCustomers = useMemo(() => {
    return customers.filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.phone1.includes(search) ||
      c.customerCode.includes(search)
    );
  }, [customers, search]);

  const columns = [
    {
      title: t('name'),
      dataIndex: 'name',
    },
    {
      title: t('phone1'),
      dataIndex: 'phone1',
    },
    {
      title: t('phone2'),
      dataIndex: 'phone2',
    },
    {
      title: t('customerCode'),
      dataIndex: 'customerCode',
    },
    {
      title: t('city'),
      render: (_: any, record: Customer) => getLocalizedCityName(record),
    },
    {
      title: t('deliveryCost'),
      dataIndex: 'deliveryCost',
      render: (cost: number) => cost ? `${cost} IQD` : '-',
    },
    {
      title: t('actions'),
      render: (_: any, record: Customer) => (
        <div
          style={{
            display: 'flex',
            justifyContent: i18n.language === 'ar' || i18n.language === 'ckb' ? 'flex-end' : 'flex-start',
          }}
        >
          <Space>
            {hasPermission("customer.update") && <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            >
              {t('edit')}
            </Button>}
            {hasPermission("customer.delete") && <Popconfirm
              title={t('sure_to_delete') as string}
              onConfirm={() => deleteCustomer(record.id)}
            >
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
              >
                {t('delete')}
              </Button>
            </Popconfirm>}
          </Space>
        </div>
      ),
    },
  ];

  return (
    <div
      style={{
        overflowX: 'auto',
        direction: i18n.language === 'ar' || i18n.language === 'ckb' ? 'rtl' : 'ltr',
      }}
    >
      <Input.Search
        allowClear
        style={{ marginBottom: 16, maxWidth: 300 }}
        placeholder={t('search_customers') as string}
        onChange={(e) => setSearch(e.target.value)}
        value={search}
      />
      <Table
        rowKey="id"
        dataSource={filteredCustomers}
        columns={columns}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 6 }}
      />
    </div>
  );
};

export default CustomerList;
